import PropTypes from 'prop-types'
import React, { PureComponent } from 'react'
import { destroy, reset, change } from 'redux-form'
import { connect } from 'react-redux'
import queryString from 'query-string'
import { get, set, has } from 'lodash'
import { compose } from 'recompose'

import { Title } from 'components/misc'
import { PaymentFormLoader } from 'components/loaders'
import { Header, LayoutPlain } from 'components/layout'
import { meta } from 'helpers/decorators'
import { PaymentInfo } from 'containers/forms/participant'
import {
  apiPayment,
  apiAwards,
  apiCodeLists,
  apiCommon,
  apiConfiguration
} from 'helpers/decorators/api'
import { getAwardQuestions } from 'redux/modules/api/award-units'
import { LEVELS, CONFIGURATION_KEYS } from 'constants/enums'
import { getRegistrationAgeKeys } from 'helpers/getConfigKeys'
import config from 'config'

const formName = 'paymentForm'

const enhance = compose(
  meta({
    title: 'Participant registration fees payment',
    intlKey: 'moduleTitle.payment'
  }),
  apiPayment({ formName }),
  apiConfiguration(),
  apiCodeLists(['genders', 'employmentStatuses', 'nationalities'], {
    public: true
  }),
  apiCommon({
    publicCountries: true,
    fetchOnClient: true
  }),
  apiAwards({
    publicLevels: true
  }),
  connect(
    state => ({
      profileOptions: get(state.api.awardUnits, 'questions', [])
    }),
    { destroy, reset, change, getAwardQuestions }
  )
)

class ParticipantReview extends PureComponent {
  static propTypes = {
    awards: PropTypes.object,
    change: PropTypes.func,
    codeLists: PropTypes.object,
    common: PropTypes.object,
    destroy: PropTypes.func,
    formValues: PropTypes.object,
    getAwardQuestions: PropTypes.func,
    isConsentGiven: PropTypes.bool,
    isExpired: PropTypes.bool,
    location: PropTypes.object,
    normalizedParticipant: PropTypes.object,
    onGetParticipant: PropTypes.func,
    onUpdateInfo: PropTypes.func,
    participant: PropTypes.object,
    profileOptions: PropTypes.array,
    push: PropTypes.func,
    reset: PropTypes.func,
    setAuthCode: PropTypes.func,
    configuration: PropTypes.object,
    handleGetRegistrationFieldsCustomization: PropTypes.func.isRequired,
    isConfigurationTrue: PropTypes.func.isRequired
  }

  static defaultProps = {
    participant: {}
  }

  constructor() {
    super()
    this.state = {
      isEditing: false,
      isLoading: true
    }
  }

  componentDidMount() {
    const query = queryString.parse(get(this.props, 'location.search'))
    const authCode = get(query, 'authorizationCode')

    if (!authCode) {
      this.props.push(`${config.publicPath}/`)
    }

    return new Promise((resolve, reject) =>
      this.props
        .setAuthCode(authCode, true)
        .then(res => {
          if (!res) {
            return this.props.onGetParticipant()
          }
        })
        .then(({ body }) => {
          if(!body){
            this.props.push(`${config.publicPath}/register/participant/review/denied`)
          }
          const orgId = get(body, 'organizationId')

          const { participant } = this.props

          const organizationId = get(participant, 'organizationId')

          this.handleLoadFieldCustomizations(organizationId, get(participant, 'awardLevel.value'))
          return this.props.getAwardQuestions(orgId)
        })
        .then(() => resolve(this.setState({ isLoading: false })))
        .catch(reject)
    ).catch(err => {
      console.error(err)
      this.setState({ isLoading: false })
    })
  }

  componentWillReceiveProps (nextProps) {
    if(get(this.props, 'participant.organizationId') !== get(nextProps, 'participant.organizationId')){
      this.handleLoadFieldCustomizations(
        get(nextProps, 'participant.organizationId'),
        get(nextProps, 'participant.awardLevel.value')
      )
    }
  }

  componentWillUnmount() {
    this.props.destroy(formName)
  }

  handleLoadFieldCustomizations = async(orgId, awardLevelValue) => {
    const ageKey = getRegistrationAgeKeys(awardLevelValue)
    const ageKeys = ageKey.min && ageKey.max ? [ageKey.min, ageKey.max] : []

    await this.props.handleGetRegistrationFieldsCustomization(orgId, ageKeys)
  }


  handleUpdateInfo = fields => {
    this.props.onUpdateInfo(fields)
    this.handleToggleEdit()
  }

  handleInfoSubmit = () => {
    this.setState({ isEditing: false })

    const oldQuery = queryString.parse(get(this.props, 'location.search'))
    const authCode = get(oldQuery, 'authorizationCode')
    const locale = get(oldQuery, 'locale')

    const query = queryString.stringify({
      authorizationCode: authCode,
      locale
    })

    window.location.href = `${
      config.boUrl
    }/register/participant/parental-consent?${query}`
  }

  handleToggleEdit = () => {
    this.setState({ isEditing: !this.state.isEditing })
    this.props.reset(formName)
  }

  handleCancelEdit = () => {
    this.setState({ isEditing: false })
    this.props.reset(formName)
  }

  filterAwardLevels = () => {
    return get(this.props.awards, 'levels').filter(level => {
      const selected = get(this.props.formValues, 'awardLevel.value')

      if (
        selected === LEVELS.BRONZE.id ||
        (level.value === LEVELS.GOLD.id && selected === LEVELS.SILVER.id)
      ) {
        return false
      }

      return level.value !== selected
    })
  }

  handlePrevLevelChange = () => {
    this.props.change(formName, 'previousCompletedAwardLevelId', '')
    this.props.change(formName, 'previousCompletedAwardPlace', '')
  }

  handleSelectBronze = () => {
    this.handlePrevLevelChange()
    this.props.change(formName, 'helpers.previousCompletedAward.value', 'no')
  }

  render() {
    const {
      awards,
      codeLists: { genders, nationalities, employmentStatuses },
      common,
      normalizedParticipant,
      participant,
      isExpired,
      isConsentGiven,
      profileOptions,
      configuration
    } = this.props

    const filteredAwardLevels = this.filterAwardLevels()

    const prevLevel = get(participant, 'previousCompletedAwardLevel.value')
    const postalAddress = get(configuration,`values[${CONFIGURATION_KEYS.postalAddress.id}].booleanValue`)
    const showTitle = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationTitle.id)
    const showSecondaryEmail = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationEmailAddress2.id)
    const showPhone = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationPhoneNumber.id)
    const showPhoneType = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationPhoneType.id)
    const showMiddleName = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationMiddleName.id)
    const showNationality = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationNationality.id)
    const showEmploymentStatus = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationEmplStatus.id)
    const showAddressLine1 = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationAddressLine1.id)
    const showAddressLine2 = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationAddressLine2.id)
    const showCity = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationAddressCity.id)
    const showZipCode = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationAddressZipCode.id)
    const showRegion = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationAddressRegion.id)
    const showCountry = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationAddressCountry.id)

    if (has(participant, 'previousCompletedAwardLevel') && !prevLevel) {
      set(participant, 'previousCompletedAwardLevel.value', 'no')
    }

    const showExpiredMessage = isExpired && !this.state.isLoading

    const {
      postalLine1,
      postalLine2,
      postalCity,
      postalZipCode,
      postalStateOrProvinceOrRegion,
      postalCountryId,
      ...rest
    } = participant || {}

    const initialValues = {
      ...rest,
      postal: {
        line1: postalLine1,
        line2: postalLine2,
        city: postalCity,
        zipCode: postalZipCode,
        stateOrProvinceOrRegion: postalStateOrProvinceOrRegion,
        countryId: postalCountryId
      },
      helpers: {
        postalAddressChecked: postalAddress
      }
    }

    return (
      <LayoutPlain greyBg header={<Header />}>
        <Title
          h1
          intlKey="moduleTitle.review_participant_info"
          className="base-margin--top"
        >
          Review participant info
        </Title>

        {showExpiredMessage && (
          <Title intlKey="label.url_no_longer_valid" h2>
            This url is no longer valid
          </Title>
        )}

        {isConsentGiven && (
          <Title intlKey="label.consent_given_login_as_child" h2>
            Consent was already given. Please login as your child.
          </Title>
        )}

        {this.state.isLoading && !isExpired && <PaymentFormLoader />}

        {!this.state.isLoading && !isExpired && !isConsentGiven && (
          <div>
            <PaymentInfo
              {...this.props}
              awards={awards}
              filteredAwardLevels={filteredAwardLevels}
              onPrevLevelChange={this.handlePrevLevelChange}
              countries={common.countries.list}
              employmentStatuses={employmentStatuses.list}
              genders={genders.list}
              initialValues={initialValues}
              isEditing={this.state.isEditing}
              nationalities={nationalities.list}
              onEdit={this.handleToggleEdit}
              onSaveChanges={this.handleUpdateInfo}
              onSubmit={this.handleInfoSubmit}
              onCancelEdit={this.handleCancelEdit}
              onSelectBronze={this.handleSelectBronze}
              participant={normalizedParticipant}
              profileOptions={profileOptions}
              postalAddress={postalAddress}
              showTitle={showTitle}
              showNationality={showNationality}
              showEmploymentStatus={showEmploymentStatus}
              showMiddleName={showMiddleName}
              showAddressLine1={showAddressLine1}
              showAddressLine2={showAddressLine2}
              showSecondaryEmail={showSecondaryEmail}
              showPhone={showPhone}
              showPhoneType={showPhoneType}
              showCity={showCity}
              showZipCode={showZipCode}
              showRegion={showRegion}
              showCountry={showCountry}
              questions={get(participant, 'profileQuestionAnswers', [])}
            />
          </div>
        )}
      </LayoutPlain>
    )
  }
}

export default enhance(ParticipantReview)
